import styled, { css, CSSProperties, keyframes } from 'styled-components';

import { Text } from '../../../../components/ToolkitV2/Text/Text';
import { Button } from '../../../../components/Toolkit/Button';

const KEYWORD_SEARCH_INPUT_HEIGHT = '48px';
const DROPDOWN_MAX_HEIGHT = '337px';

const slideDown = keyframes`
  from {
    max-height: 0;
    opacity: 0;
  }
  to {
    max-height: ${DROPDOWN_MAX_HEIGHT};
    opacity: 1;
  }
`;

export const Heading = styled(Text)`
  padding: ${({ theme }) => `${theme.spacing.S4} ${theme.spacing.M16}`};
  background-color: ${({ theme }) => theme.donedeal.colors.GREY_LIGHTER};
`;

export const ListItem = styled.li<{ highlighted: boolean }>`
  width: 100%;
  ${({ theme }) => theme.donedeal.fontSize.M16}
  padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M16}`};
  border-bottom: 1px solid ${({ theme }) => theme.donedeal.colors.GREY_LIGHTER};
  background-color: ${({ theme, highlighted }) =>
    highlighted
      ? theme.donedeal.colors.GREY_LIGHTER
      : theme.donedeal.colors.WHITE};
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${({ theme, highlighted }) =>
      highlighted
        ? theme.donedeal.colors.GREY_LIGHTER
        : theme.donedeal.colors.WHITE};
  }
`;

export const ClearButton = styled(Button)`
  cursor: pointer;
  color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:focus {
    outline: none;
  }

  // The SharedButton styles offset all nested SVGs by 3px for some reason, so we need to override that here to allow proper centering
  svg {
    margin-top: 0;
  }
`;

export const InputWrapper = styled.div<{ margin?: CSSProperties['margin'] }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  grid-column-gap: ${({ theme }) => theme.spacing.S8};
  align-items: center;
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};
  height: ${KEYWORD_SEARCH_INPUT_HEIGHT};
  border-radius: ${({ theme }) => theme.donedeal.borderRadius.default};
  border: 1px solid ${({ theme }) => theme.donedeal.colors.GREY};
  padding: 0 ${({ theme }) => theme.donedeal.spacing.M16};
  margin: ${({ margin }) => margin};

  &:focus-within {
    border-width: 2px;
    border-color: ${({ theme }) => theme.donedeal.colors.BLUE};
  }
`;

export const SearchIcon = styled.div`
  padding: 0;
  margin: 0;
  border: 0;
  width: 20px;
  height: 20px;
  font-size: 100%;
  line-height: 1;
  vertical-align: middle;

  svg {
    color: ${({ theme }) => theme.donedeal.colors.GREY_DARKER};
    width: 100%;
    height: 100%;
  }

  &:focus {
    outline: none;
  }
`;

export const KeywordSearchInput = styled.input`
  outline: none;
  border: 0;
  padding: 0;
  ${({ theme }) => theme.donedeal.fontSize.M16};
  background-color: inherit;

  &::placeholder {
    color: ${({ theme }) => theme.donedeal.colors.GREY_DARK};
  }
`;

export const List = styled.ul<{
  variant?: 'modal' | 'dropdown';
}>`
  overflow-y: auto;
  max-height: calc(100% - ${KEYWORD_SEARCH_INPUT_HEIGHT});
  height: 100%;
  padding-top: ${({ theme }) => theme.donedeal.spacing.M16};
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};

  ${({ theme, variant }) =>
    variant === 'dropdown' &&
    css`
      height: auto;
      max-height: ${DROPDOWN_MAX_HEIGHT};
      border-radius: ${`0 0 ${theme.donedeal.borderRadius.default} ${theme.donedeal.borderRadius.default}`};
      box-shadow: ${theme.donedeal.shadow.CONTAINER};
      animation: ${slideDown} 200ms ease-out forwards;

      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    `}
`;

export const EmptyList = styled.div`
  margin-top: ${({ theme }) => theme.donedeal.spacing.S8};
  margin-bottom: ${({ theme }) => theme.donedeal.spacing.S8};
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};

  & div {
    padding: ${({ theme }) =>
      `${theme.donedeal.spacing.S8} ${theme.donedeal.spacing.M16}`};
    display: flex;
    align-items: center;
  }
`;

export const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.donedeal.colors.WHITE};
  text-align: center;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: ${({ theme }) => `calc(337px - ${theme.donedeal.spacing.M16})`};
`;

export const ErrorListContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 132px;
  width: 312px;
  gap: 4px;
`;

export const SkeletonListItem = styled.div`
  height: 48px;
  padding: ${({ theme }) => `${theme.spacing.S12} ${theme.spacing.M16}`};
  border-bottom: 1px solid ${({ theme }) => theme.donedeal.colors.GREY_LIGHTER};
`;
